import React, { useState, useEffect } from "react";
import {
  Box, Typography, Button, Dialog, DialogTitle, DialogContent,
  TextField, IconButton, Grid, Stack, Table, TableBody, 
  TableCell, TableContainer, TableHead, TableRow, Paper, 
  TablePagination, Divider, Chip, MenuItem, CircularProgress
} from "@mui/material";
import {
  Add as AddIcon, Search as SearchIcon, Edit as EditIcon,
  Delete as DeleteIcon, Apartment as HostelIcon, Person as WardenIcon
} from "@mui/icons-material";
import { db } from "../../firebase"; 
import { 
  collection, addDoc, getDocs, query, orderBy, doc, 
  updateDoc, deleteDoc, Timestamp, where 
} from "firebase/firestore";
import Swal from "sweetalert2";

const PRIMARY = "#0d9488";

function Hosteldetails() {
  const [hostels, setHostels] = useState([]);
  const [wardens, setWardens] = useState([]); // State for Warden List
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const currentSchoolId = "zIlbJM5BjyrlEhg7fWKs";

  const [form, setForm] = useState({
    hostelName: "",
    address: "Kavathe Mahankal",
    capacity: "",
    type: "Boys",
    status: "Active",
    wardenId: "", // New Field
    wardenName: "", // Store name for quick display
    schoolId: currentSchoolId
  });

  useEffect(() => { 
    fetchHostels(); 
    fetchWardens(); 
  }, []);

  // 1. Fetch Wardens from Users collection
  const fetchWardens = async () => {
    try {
      const q = query(
        collection(db, "users"), 
        where("role", "==", "Warden")
        // where("schoolId", "==", currentSchoolId) // Optional: filter by school if needed
      );
      const snap = await getDocs(q);
      setWardens(snap.docs.map(doc => ({ id: doc.id, name: doc.data().name })));
    } catch (err) {
      console.error("Warden Fetch Error:", err);
    }
  };

  const fetchHostels = async () => {
    setLoading(true);
    try {
      const q = query(
        collection(db, "hostels"), 
        where("schoolId", "==", currentSchoolId),
      );
      const snap = await getDocs(q);
      setHostels(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    } catch (err) { 
      console.error("Firestore Error:", err); 
    }
    setLoading(false);
  };

  const handleSubmit = async () => {
    if (!form.hostelName || !form.capacity || !form.wardenId) {
      return Swal.fire("Error", "Hostel Name, Capacity, and Warden are required", "error");
    }
    setLoading(true);
    try {
      // Find selected warden name to save in hostel record
      const selectedWarden = wardens.find(w => w.id === form.wardenId);
      const payload = { 
        ...form, 
        wardenName: selectedWarden ? selectedWarden.name : "",
        updatedAt: Timestamp.now() 
      };

      if (editId) {
        await updateDoc(doc(db, "hostels", editId), payload);
        Swal.fire("Updated", "Hostel details updated", "success");
      } else {
        await addDoc(collection(db, "hostels"), payload);
        Swal.fire("Saved", "New hostel added", "success");
      }
      setOpen(false);
      resetForm();
      fetchHostels();
    } catch (err) { 
      Swal.fire("Error", err.message, "error"); 
    }
    setLoading(false);
  };

  const handleDelete = async (id) => {
    const confirm = await Swal.fire({
      title: "Delete Hostel?",
      text: "This will remove the hostel record.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33"
    });
    if (confirm.isConfirmed) {
      await deleteDoc(doc(db, "hostels", id));
      fetchHostels();
      Swal.fire("Deleted", "Record removed", "success");
    }
  };

  const handleEdit = (row) => {
    setEditId(row.id);
    setForm({ ...row });
    setOpen(true);
  };

  const resetForm = () => {
    setEditId(null);
    setForm({
      hostelName: "",
      address: "Kavathe Mahankal",
      capacity: "",
      type: "Boys",
      status: "Active",
      wardenId: "",
      wardenName: "",
      schoolId: currentSchoolId
    });
  };

  const filteredData = hostels.filter(h =>
    h.hostelName.toLowerCase().includes(search.toLowerCase()) ||
    (h.wardenName && h.wardenName.toLowerCase().includes(search.toLowerCase()))
  );

  return (
    <Box p={1} sx={{ background: "#f8fafc", minHeight: "100vh" }}>
      <Stack direction="row" justifyContent="space-between" mb={3} alignItems="center">
        <Box>
            <Typography variant="h6" fontWeight="bold">Hostel Management</Typography>
            <Typography variant="caption" color="text.secondary">Manage accommodation and Warden assignments</Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} sx={{ bgcolor: PRIMARY, borderRadius: 2 }} onClick={() => { resetForm(); setOpen(true); }}>
          Add Hostel
        </Button>
      </Stack>

      <Paper sx={{ borderRadius: 3, overflow: "hidden", border: "1px solid #e2e8f0" }} elevation={0}>
        <Box p={2}>
          <TextField 
            fullWidth size="small" placeholder="Search by name, location or warden..." 
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{ startAdornment: <SearchIcon sx={{ mr: 1, color: "gray" }} /> }}
          />
        </Box>

        <TableContainer>
          <Table size="small">
            <TableHead sx={{ bgcolor: "#f1f5f9" }}>
              <TableRow>
                <TableCell><b>Sr.</b></TableCell>
                <TableCell><b>Hostel Name</b></TableCell>
                <TableCell><b>Warden</b></TableCell>
                <TableCell><b>Type/Capacity</b></TableCell>
                <TableCell><b>Status</b></TableCell>
                <TableCell align="center"><b>Actions</b></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={6} align="center"><CircularProgress size={24} sx={{ my: 2 }} /></TableCell></TableRow>
              ) : (
                filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row, index) => (
                  <TableRow key={row.id} hover>
                    <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                    <TableCell>
                        <Typography variant="body2" fontWeight="bold">{row.hostelName}</Typography>
                        <Typography variant="caption" color="text.secondary">{row.address}</Typography>
                    </TableCell>
                    <TableCell>
                        <Stack direction="row" alignItems="center" gap={0.5}>
                            <WardenIcon sx={{ fontSize: 16, color: PRIMARY }} />
                            <Typography variant="body2">{row.wardenName || "Not Assigned"}</Typography>
                        </Stack>
                    </TableCell>
                    <TableCell>
                        <Chip label={row.type} size="small" sx={{ mr: 1, height: 20 }} />
                        <Typography variant="caption">{row.capacity} Beds</Typography>
                    </TableCell>
                    <TableCell>
                        <Chip label={row.status} size="small" color={row.status === "Active" ? "success" : "error"} variant="outlined" />
                    </TableCell>
                    <TableCell align="center">
                      <IconButton size="small" onClick={() => handleEdit(row)} color="primary"><EditIcon fontSize="small" /></IconButton>
                      <IconButton size="small" onClick={() => handleDelete(row.id)} color="error"><DeleteIcon fontSize="small" /></IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination component="div" count={filteredData.length} rowsPerPage={rowsPerPage} page={page} onPageChange={(e, n) => setPage(n)} />
      </Paper>

      {/* FORM DIALOG */}
      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <HostelIcon color="primary" />
          <Typography variant="h6" fontWeight="bold">{editId ? "Update Hostel" : "New Hostel Entry"}</Typography>
        </DialogTitle>
        <Divider />
        <DialogContent>
          <Grid className="row" spacing={2} mt={1}>
            <Grid className="col-sm-6 mb-3" item xs={12}>
              <TextField label="Hostel Name" fullWidth size="small" value={form.hostelName} onChange={(e) => setForm({...form, hostelName: e.target.value})} />
            </Grid>
            
            <Grid className="col-sm-6 mb-3" item xs={12}>
              {/* WARDEN SELECTION DROPDOWN */}
              <TextField 
                select 
                label="Assign Warden" 
                fullWidth size="small" 
                value={form.wardenId} 
                onChange={(e) => setForm({...form, wardenId: e.target.value})}
              >
                {wardens.length > 0 ? wardens.map(w => (
                    <MenuItem key={w.id} value={w.id}>{w.name}</MenuItem>
                )) : <MenuItem disabled>No Wardens Found</MenuItem>}
              </TextField>
            </Grid>

            <Grid className="col-sm-6 mb-3" item xs={12} sm={6}>
              <TextField select label="Hostel Type" fullWidth size="small" value={form.type} onChange={(e) => setForm({...form, type: e.target.value})}>
                <MenuItem value="Boys">Boys</MenuItem>
                <MenuItem value="Girls">Girls</MenuItem>
                <MenuItem value="Co-ed">Co-ed</MenuItem>
              </TextField>
            </Grid>
            <Grid className="col-sm-6 mb-3" item xs={12} sm={6}>
              <TextField label="Student Capacity" type="number" fullWidth size="small" value={form.capacity} onChange={(e) => setForm({...form, capacity: e.target.value})} />
            </Grid>
            <Grid className="col-sm-6 mb-3" item xs={12}>
              <TextField label="Full Address" multiline rows={2} fullWidth size="small" value={form.address} onChange={(e) => setForm({...form, address: e.target.value})} />
            </Grid>
            <Grid className="col-sm-6 mb-3" item xs={12}>
              <TextField select label="Status" fullWidth size="small" value={form.status} onChange={(e) => setForm({...form, status: e.target.value})}>
                <MenuItem value="Active">Active</MenuItem>
                <MenuItem value="Inactive">Inactive</MenuItem>
              </TextField>
            </Grid>
          </Grid>

          <Button 
            variant="contained" 
            fullWidth 
            sx={{ mt: 3, py: 1.5, borderRadius: 2, bgcolor: PRIMARY, fontWeight: "bold" }} 
            onClick={handleSubmit} 
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : (editId ? "Update Hostel Record" : "Create Hostel")}
          </Button>
        </DialogContent>
      </Dialog>
    </Box>
  );
}

export default Hosteldetails;